/**
 * NEMA Filter Engine v1 — side panel definitions (June 2025 spec).
 * Visibility is conditional on care type selected on the homepage.
 */

import { SHIFT_12_DAY_WINDOWS, SHIFT_12_NIGHT_WINDOWS } from "./careShiftWindows";
import { getNowInAppTimezone } from "../utils/dateFormat";

export const CARE_TYPES = {
  attendant: { id: "attendant", label: "Care Attendant", icon: "🩺" },
  nurses: { id: "nurses", label: "Nurse", icon: "💉" },
  physio: { id: "physio", label: "Physiotherapist", icon: "🦴" },
  dementia: { id: "dementia", label: "Dementia", icon: "🧠" },
  post_operative: { id: "post_operative", label: "Post Operative", icon: "🏥" },
};

export const PLAN_TYPES = [
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
];

export function formatSearchTimeLabel(hhmm) {
  const [hRaw, mRaw] = String(hhmm || "").split(":");
  const h = Number(hRaw);
  const m = Number(mRaw);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return hhmm || "";
  const period = h >= 12 ? "PM" : "AM";
  const hour12 = h % 12 || 12;
  return `${hour12}:${String(m).padStart(2, "0")} ${period}`;
}

/** Start times for marketplace search (24h HH:mm). */
export const SEARCH_TIME_OPTIONS = [
  "06:00",
  "06:30",
  "07:00",
  "07:30",
  "08:00",
  "08:30",
  "09:00",
  "09:30",
  "10:00",
  "10:30",
  "11:00",
  "11:30",
  "12:00",
  "12:30",
  "13:00",
  "13:30",
  "14:00",
  "14:30",
  "15:00",
  "15:30",
  "16:00",
  "16:30",
  "17:00",
  "17:30",
  "18:00",
  "18:30",
  "19:00",
  "19:30",
  "20:00",
  "20:30",
  "21:00",
  "21:30",
  "22:00",
].map((value) => ({ value, label: formatSearchTimeLabel(value) }));

function buildTimeOptionsInRange(startHhmm, endHhmm, stepMinutes) {
  const [startH, startM] = startHhmm.split(":").map(Number);
  const [endH, endM] = endHhmm.split(":").map(Number);
  const startTotal = startH * 60 + startM;
  const endTotal = endH * 60 + endM;
  const options = [];
  for (let mins = startTotal; mins <= endTotal; mins += stepMinutes) {
    const value = `${String(Math.floor(mins / 60)).padStart(2, "0")}:${String(mins % 60).padStart(2, "0")}`;
    options.push({ value, label: formatSearchTimeLabel(value) });
  }
  return options;
}

/** 12 Hrs Day / Night only start at these exact fixed windows — show the full start–end range. */
const SEARCH_TIME_OPTIONS_12_DAY = SHIFT_12_DAY_WINDOWS.map((w) => ({ value: w.start, label: w.label }));
const SEARCH_TIME_OPTIONS_12_NIGHT = SHIFT_12_NIGHT_WINDOWS.map((w) => ({
  value: w.start,
  // Drop the "next day" suffix here — it wraps awkwardly in this dropdown's narrow width.
  label: w.label.replace(/\s*next day\s*$/i, ""),
}));
const SEARCH_TIME_OPTIONS_HOURLY = buildTimeOptionsInRange("07:00", "21:00", 30);
const SEARCH_TIME_OPTIONS_24H = buildTimeOptionsInRange("08:00", "18:00", 60);

/**
 * Search-bar "Time" options for the selected shift type — each shift has a different
 * valid window. When `searchDate` is today, drop start times already in the past so
 * the customer can't pick a slot that's already gone (for any shift type).
 */
export function getSearchTimeOptionsForShift(searchShift, searchDate) {
  const shift = String(searchShift || "").trim().toLowerCase();
  const base =
    shift === "hourly"
      ? SEARCH_TIME_OPTIONS_HOURLY
      : shift === "12_day"
        ? SEARCH_TIME_OPTIONS_12_DAY
        : shift === "12_night"
          ? SEARCH_TIME_OPTIONS_12_NIGHT
          : shift === "24h"
            ? SEARCH_TIME_OPTIONS_24H
            : SEARCH_TIME_OPTIONS;

  if (!searchDate || searchDate !== getMarketplaceTodayIsoDate()) return base;
  // Compare against the app's configured timezone (IST) — not the visitor's own
  // device/OS clock — so this list matches the backend's IST-based booking logic.
  const { minutesSinceMidnight: nowMin } = getNowInAppTimezone();
  if (nowMin == null) return base;
  const nowHhmm = `${String(Math.floor(nowMin / 60)).padStart(2, "0")}:${String(nowMin % 60).padStart(2, "0")}`;
  // No fallback to the full list here: if every window for today has already
  // passed (e.g. it's 6pm and 12hr-day only offers 7/8/9am starts), there is
  // genuinely nothing left to book today for this shift — showing the past
  // options back to the customer would let them pick a start time that's
  // already gone. The caller should prompt picking another date/shift instead.
  return base.filter((opt) => opt.value > nowHhmm);
}

/** Care shift / duration options for marketplace search. */
export const SEARCH_SHIFT_OPTIONS = [
  { value: "hourly", label: "Hourly", hours: 4, planType: "daily", shiftType: "hourly" },
  { value: "12_day", label: "12 hour - day shift", hours: 12, planType: "daily", shiftType: "day" },
  { value: "12_night", label: "12 hour - night shift", hours: 12, planType: "daily", shiftType: "night" },
  { value: "24h", label: "24 hours live in", hours: 24, planType: "weekly", shiftType: "24hour" },
];

/** @deprecated use SEARCH_SHIFT_OPTIONS */
export const SEARCH_HOURS_OPTIONS = SEARCH_SHIFT_OPTIONS.map((o) => ({
  value: o.hours,
  label: o.label,
}));

export const DEFAULT_SEARCH_SHIFT = "24h";
export const DEFAULT_SEARCH_HOURS = SEARCH_SHIFT_OPTIONS.find((o) => o.value === DEFAULT_SEARCH_SHIFT)?.hours ?? 24;
export const DEFAULT_MARKETPLACE_CARE_TYPE = "attendant";
/** Default discovery radius when searching by locality/area (km). */
export const DEFAULT_MARKETPLACE_RADIUS_KM = 15;
/**
 * City-centre picks (e.g. "Bengaluru" with no locality) need a wider metro band —
 * caregivers often sit 8–20 km from the catalog city pin.
 */
export const CITY_MARKETPLACE_RADIUS_KM = 25;
/** GPS “Near me” / patient-address nearby band (km). */
export const NEAR_ME_RADIUS_KM = 12;

/** Resolve API search radius for a catalog city/area selection. */
export function marketplaceRadiusForLocation(city) {
  if (city && !city.area) return CITY_MARKETPLACE_RADIUS_KM;
  return DEFAULT_MARKETPLACE_RADIUS_KM;
}

/** Today's calendar date (app timezone / IST) as YYYY-MM-DD for marketplace search defaults. */
export function getMarketplaceTodayIsoDate() {
  return getNowInAppTimezone().dateIso;
}

/**
 * Current wall-clock time (app timezone) as "HH:MM", for search entry points where
 * an untouched time filter should mean "right now" (e.g. browsing availability),
 * not a fixed placeholder — so the backend's occupancy check reflects the actual
 * current instant instead of always evaluating a stale default time.
 */
export function getMarketplaceNowHhMm() {
  const minutes = getNowInAppTimezone().minutesSinceMidnight;
  if (!Number.isFinite(minutes)) return DEFAULT_SEARCH_TIME;
  const hh = Math.floor(minutes / 60) % 24;
  const mm = minutes % 60;
  return `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
}

export const DEFAULT_SEARCH_TIME = "10:00";

export function getSearchShiftOption(shift) {
  const key = String(shift ?? "");
  return (
    SEARCH_SHIFT_OPTIONS.find((o) => o.value === key) ||
    SEARCH_SHIFT_OPTIONS.find((o) => o.hours === Number(shift)) ||
    SEARCH_SHIFT_OPTIONS[0]
  );
}

export function planTypeFromSearchShift(shift) {
  return getSearchShiftOption(shift).planType || "daily";
}

export function searchShiftToHours(shift) {
  return getSearchShiftOption(shift).hours ?? 4;
}

export function searchShiftToShiftType(shift) {
  return getSearchShiftOption(shift).shiftType || undefined;
}

/** Inclusive calendar-day window for registered-member booking (Excel: 7 days). */
export const MARKETPLACE_BOOKING_WINDOW_DAYS = 7;

export function getMarketplaceMaxIsoDate(windowDays = MARKETPLACE_BOOKING_WINDOW_DAYS) {
  const days = Math.max(1, Number(windowDays) || MARKETPLACE_BOOKING_WINDOW_DAYS);
  const d = new Date();
  d.setDate(d.getDate() + (days - 1));
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function planTypeFromSearchHours(hoursOrShift) {
  if (typeof hoursOrShift === "string" && SEARCH_SHIFT_OPTIONS.some((o) => o.value === hoursOrShift)) {
    return planTypeFromSearchShift(hoursOrShift);
  }
  const n = Number(hoursOrShift);
  if (n >= 24) return "weekly";
  if (n >= 4) return "daily";
  return "monthly";
}

export const SHIFT_TYPES = [
  { value: "hourly", label: "Hourly" },
  { value: "day", label: "12 hour - day shift" },
  { value: "night", label: "12 hour - night shift" },
  { value: "24hour", label: "24 hours live in" },
];

export const PATIENT_CONDITIONS = [
  "Elderly Care",
  "Bedridden",
  "Stroke",
  "Paralysis",
  "Parkinson's",
  "Dementia",
  "Alzheimer's",
  "Post Surgery",
  "Cancer Care",
  "Palliative Care",
  "Orthopedic",
  "Tracheostomy",
  "Feeding Tube",
  "Catheter Care",
];

export const LANGUAGES = [
  "Hindi",
  "English",
  "Kannada",
  "Tamil",
  "Telugu",
  "Malayalam",
  "Marathi",
  "Bengali",
  "Gujarati",
  "Punjabi",
];

export const GENDER_OPTIONS = [
  { value: "female", label: "Female" },
  { value: "male", label: "Male" },
  { value: "any", label: "No Preference" },
];

export const AGE_GROUPS = [
  { value: "18-24", label: "18–24", min: 18, max: 24 },
  { value: "25-34", label: "25–34", min: 25, max: 34 },
  { value: "35-45", label: "35–45", min: 35, max: 45 },
  { value: "45+", label: "45+", min: 45, max: 120 },
];

export const EXPERIENCE_OPTIONS = [
  { value: 1, label: "1+ years" },
  { value: 3, label: "3+ years" },
  { value: 5, label: "5+ years" },
  { value: 10, label: "10+ years" },
];

export const SKILLS_COMMON = [
  "BP Monitoring",
  "Sugar Monitoring",
  "Injection Assistance",
  "Oxygen Support",
  "Catheter Care",
  "Diaper Management",
  "Feeding Assistance",
  "Wheelchair Transfer",
  "Mobility Support",
  "Exercise Assistance",
];

export const RELIGION_OPTIONS = [
  { value: "any", label: "No Preference" },
  { value: "hindu", label: "Hindu" },
  { value: "muslim", label: "Muslim" },
  { value: "sikh", label: "Sikh" },
  { value: "christian", label: "Christian" },
  { value: "jain", label: "Jain" },
];

export const FOOD_OPTIONS = [
  { value: "vegetarian", label: "Vegetarian" },
  { value: "non_vegetarian", label: "Non-Vegetarian" },
  { value: "any", label: "No Preference" },
];

export const SLEEP_OPTIONS = [
  { value: "separate", label: "Separate Room Available" },
  { value: "shared", label: "Shared Room with Elderly / Patient" },
];

export const NURSE_CLINICAL = [
  "Post Hospitalisation",
  "ICU Trained",
  "Tracheostomy",
  "Wound Dressing",
  "Oxygen Support",
  "Injection Administration",
  "Diabetes Management",
  "Catheter Care",
  "Feeding Tube",
];

export const NURSE_QUALIFICATIONS = [
  "ANM (Auxiliary Nurse Midwifery)",
  "GNM (General Nursing and Midwifery)",
  "B.Sc Nursing",
  "ICU Trained Nurse",
];

export const NURSE_SKILLS = [
  "IV Line Management",
  "PEG Feed",
  "Stoma Bag",
  "Suctioning",
  "Bedside Monitoring",
];

export const PHYSIO_REQUIREMENTS = [
  "Stroke Rehabilitation",
  "Knee Replacement",
  "Hip Replacement",
  "Frozen Shoulder",
  "Back Pain",
  "Cervical Pain",
  "Parkinson's",
  "Sports Injury",
  "Balance Training",
  "Geriatric Physio",
];

export const PHYSIO_FREQUENCY = ["Daily", "Alternate Days", "Twice Weekly", "Weekly"];

export const PHYSIO_DURATION = ["30 mins", "45 mins", "60 mins", "90 mins"];

/** CareHub consultation modality (Platform Paradigm customer search). */
export const CONSULTATION_TYPES = [
  { value: "home_visit", label: "Home Visit" },
  { value: "tele", label: "Tele / Video" },
  { value: "clinic", label: "Clinic" },
];

export const SALARY_HOURLY_MIN = 0;
export const SALARY_MONTHLY_MIN = 0;
export const SALARY_HOURLY_MAX = 5000;
export const SALARY_MONTHLY_MAX = 100000;

export function createDefaultFilterState(careType = DEFAULT_MARKETPLACE_CARE_TYPE) {
  return {
    careType,
    patientConditions: [],
    languages: [],
    gender: "any",
    ageGroup: "",
    minExperience: 0,
    verifiedOnly: false,
    skills: [],
    religion: "any",
    foodPreference: "any",
    sleepArrangement: "",
    availableNow: false,
    salaryHourlyMin: SALARY_HOURLY_MIN,
    salaryMonthlyMin: SALARY_MONTHLY_MIN,
    salaryHourlyMax: SALARY_HOURLY_MAX,
    salaryMonthlyMax: SALARY_MONTHLY_MAX,
    salaryServiceType: "both",
    minRating: 0,
    shiftType: careType === "physio" ? "" : searchShiftToShiftType(DEFAULT_SEARCH_SHIFT) || "",
    nurseClinical: [],
    nurseQualifications: [],
    nurseSkills: [],
    physioRequirements: [],
    physioFrequency: "",
    physioDuration: "",
    consultationType: "",
    moreFiltersOpen: false,
  };
}
